// A new signed-in account sees the four reasons somebody might be here and an
// optional setup card above the compass. A returning one sees what it already
// started: the plan, its clashes, the choices still worth making, saved artists,
// groups and the forum. A module renders only when it has something in it.

import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '../../data/session.tsx';
import { useApp } from '../../data/store.tsx';
import { useDecisionQueue } from '../../data/decisions.tsx';
import { minutesOfMusic } from '../../engine/picks.ts';
import { FestivalCompass } from '../components/FestivalCompass.tsx';
import { ImprovePlan } from '../components/ImprovePlan.tsx';
import { LineupChangeNotice } from '../components/LineupChangeNotice.tsx';
import '../../styles/home.css';

const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

const ENTRIES = [
  {
    to: '/plan',
    title: 'Build my plan',
    copy: 'Turn the lineup into a first-pass schedule, then refine the choices that matter.',
  },
  {
    to: '/lineup',
    title: 'Explore the lineup',
    copy: 'Save artists, rate what you know, and find names you might have missed.',
  },
  {
    to: '/search',
    title: 'Find my people',
    copy: 'Connect with friends and groups, then plan around each other when you want.',
  },
  {
    to: '/forum',
    title: 'Explore the community',
    copy: 'Browse the forum, photos, and artist showcases.',
  },
];

const SETUP_DISMISSED = 'homeSetupDismissed';

export function Home() {
  const { state, derived, storage } = useApp();
  const { session } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const { queue } = useDecisionQueue(8);
  const picks = [...derived.picksByDay.values()].flat();
  const music = minutesOfMusic(picks);
  const dayCount = [...derived.picksByDay.values()].filter((day) => day.length > 0).length;
  const crews = session?.crews ?? [];
  const [setupDismissed, setSetupDismissed] = useState<boolean | null>(null);
  const [threads, setThreads] = useState<{ id: string; title: string; username: string }[]>([]);

  useEffect(() => {
    let dropped = false;
    void storage.meta<boolean>(SETUP_DISMISSED).then((value) => {
      if (!dropped) setSetupDismissed(Boolean(value));
    });
    return () => {
      dropped = true;
    };
  }, [storage]);

  useEffect(() => {
    let dropped = false;
    fetch('/api/forum', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : { threads: [] }))
      .then((data: { threads?: { id: string; title: string; username: string }[] }) => {
        if (!dropped) setThreads((data.threads ?? []).slice(0, 3));
      })
      .catch(() => undefined);
    return () => {
      dropped = true;
    };
  }, []);

  const manual = state.ratings.filter((r) => r.userId === state.userId && r.source === 'manual');
  const accountReady = Boolean((location.state as { accountReady?: boolean } | null)?.accountReady);
  const newUser =
    accountReady ||
    (Boolean(session?.user) && picks.length === 0 && manual.length === 0 && setupDismissed === false);

  const clashDays = [...derived.clashesByDay.entries()].filter(([, list]) => list.length > 0);
  const clashCount = clashDays.reduce((total, [, list]) => total + list.length, 0);

  const saved = [
    ...derived.myMustSee.map((m) => m.artistId),
    ...manual.filter((r) => r.score >= 8).sort((a, b) => b.score - a.score).map((r) => r.artistId),
  ]
    .filter((id, index, all) => all.indexOf(id) === index)
    .map((id) => derived.artistById.get(id)?.name)
    .filter((name): name is string => Boolean(name))
    .slice(0, 6);

  const profile = state.profiles.find((p) => p.userId === state.userId);
  const sparse =
    Object.keys(profile?.subgenreWeights ?? {}).length === 0 ||
    !(profile?.goalPriorities?.length || profile?.goalPriority);

  return (
    <div className="festival-home">
      <header className="festival-home__intro">
        <p className="festival-home__eyebrow">Independent festival guide · 2026</p>
        <h1>Explore Lost Lands</h1>
        <p>Find your next set, plan your day, or catch up on the latest updates.</p>
      </header>

      {newUser && (
        <section className="home-welcome" aria-labelledby="home-welcome-heading">
          <h2 id="home-welcome-heading">You're in.</h2>
          <p>
            Build a schedule, explore the lineup, or jump into the community. Set up as much or as
            little as you want.
          </p>
          <div className="entrygrid">
            {ENTRIES.map((entry) => (
              <Link className="entrycard" to={entry.to} key={entry.to}>
                <h3>{entry.title}</h3>
                <p>{entry.copy}</p>
              </Link>
            ))}
          </div>
          {!setupDismissed && (
            <section className="panel setupcard">
              <h3>Make Lost Lands work more like you</h3>
              <p className="muted">
                Add your music taste, energy, crew, and planning preferences. Change any of it later.
              </p>
              <div className="flex wrap">
                <button className="primary" type="button" onClick={() => navigate('/preferences')}>
                  Set preferences
                </button>
                <button
                  type="button"
                  className="small ghost"
                  onClick={() => {
                    setSetupDismissed(true);
                    void storage.setMeta(SETUP_DISMISSED, true);
                  }}
                >
                  Skip for now
                </button>
              </div>
            </section>
          )}
        </section>
      )}

      <div className="festival-home__layout">
        <FestivalCompass />

        <div className="festival-home__details">
          <section className="home-plan" aria-labelledby="home-plan-heading">
            <p className="festival-home__eyebrow">{picks.length > 0 ? 'Saved on this device' : 'Start here'}</p>
            <h2 id="home-plan-heading">{picks.length > 0 ? 'Your schedule' : 'Start with the music'}</h2>
            {picks.length > 0 ? (
              <>
                <p>
                  {count(picks.length, 'set', 'sets')} across {count(dayCount, 'day', 'days')}.
                  {' '}{Math.floor(music / 60)}h {music % 60}m of music.
                </p>
                <div className="home-plan__actions">
                  <Link className="home-action home-action--primary" to="/plan">Open my schedule <span aria-hidden="true">↗</span></Link>
                  {queue.length > 0 && <Link className="home-action" to="/plan/improve">Improve my plan</Link>}
                </div>
              </>
            ) : (
              <>
                <p>Browse artists and set times, then choose what you want to see.</p>
                <p className="home-plan__numbers">
                  {count(state.artists.length, 'artist', 'artists')}<span aria-hidden="true"> · </span>{count(state.sets.length, 'set', 'sets')}
                </p>
                <div className="home-plan__actions">
                  <Link className="home-action home-action--primary" to="/lineup">Browse the lineup <span aria-hidden="true">↗</span></Link>
                  <Link className="home-action" to="/plan">Open the planner</Link>
                </div>
              </>
            )}
            {sparse && (
              <Link to="/preferences" className="home-plan__preferences">Music &amp; planning preferences <span aria-hidden="true">→</span></Link>
            )}
            {!session?.user && (
              <p className="home-plan__guest">Your picks stay in this browser. <Link to="/account">Sign in</Link> to sync them across devices.</p>
            )}
          </section>

          <LineupChangeNotice />

          {clashCount > 0 && (
            <section className="home-crews" aria-labelledby="home-clashes-heading">
              <h2 id="home-clashes-heading">Clashes</h2>
              <p>{count(clashCount, 'pair of picks overlaps or leaves a short walk', 'pairs of picks overlap or leave a short walk')}.</p>
              <Link to={`/plan?day=${encodeURIComponent(clashDays[0][0])}#clashes`}>
                Compare options <span aria-hidden="true">→</span>
              </Link>
            </section>
          )}

          {queue.length > 0 && <ImprovePlan compact />}

          {saved.length > 0 && (
            <section className="home-crews" aria-labelledby="home-saved-heading">
              <h2 id="home-saved-heading">Saved artists</h2>
              <p>{saved.join(' · ')}</p>
              <Link to="/lineup">Open the lineup <span aria-hidden="true">→</span></Link>
            </section>
          )}

          {crews.length > 0 && (
            <section className="home-crews" aria-labelledby="home-crews-heading">
              <h2 id="home-crews-heading">Your groups</h2>
              <p>{crews.map((crew) => crew.name).join(' · ')}</p>
              <Link to="/search">Find people &amp; groups <span aria-hidden="true">→</span></Link>
            </section>
          )}

          {threads.length > 0 && (
            <section className="home-crews" aria-labelledby="home-forum-heading">
              <h2 id="home-forum-heading">Recent community</h2>
              <div className="home-shortcuts__rows">
                {threads.map((thread) => (
                  <Link to="/forum" key={thread.id}>
                    <span>{thread.title}</span>
                    <span aria-hidden="true">@{thread.username}</span>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <section className="home-shortcuts" aria-labelledby="home-shortcuts-heading">
            <h2 id="home-shortcuts-heading">Around the festival</h2>
            <div className="home-shortcuts__rows">
              <Link to="/event"><span>At the stages</span><span aria-hidden="true">↗</span></Link>
              <Link to="/links"><span>Travel &amp; festival information</span><span aria-hidden="true">↗</span></Link>
              <Link to="/photos"><span>Community photos</span><span aria-hidden="true">↗</span></Link>
              <Link to="/art"><span>Artists’ corner</span><span aria-hidden="true">↗</span></Link>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
